import AddIcon from "@mui/icons-material/Add";
import { Modal, Button, Paper, Grid, Typography, Box, Alert, TextField, ButtonGroup, useTheme } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import CheckIcon from "@mui/icons-material/Check";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";

export const CrewDeviceInviteModal = (): JSX.Element => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [crewMembers, setCrewMembers] = useState<ICrewMember[]>([]);
  const inviteLimit = 10;
  const [loadingCrew, setLoadingCrew] = useState<boolean>(true);
  const [filterValue, setFilterValue] = useState<string>("");
  const dispatch = useDispatch();

  const emailInvitation = useCallback(
    async (crewId: string) => {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Creating Invitation",
        successMessage: "Invitation Sent",
        errorMessage: "Error: Unable To generate invitation",
      };
      const response = await AmplifyAPIWrapper.Post<any>(`/crew/efb/invite/${crewId}`, notifications);
      if (response) {
        return true;
      } else {
        return false;
      }
    },
    [dispatch]
  );

  const resetCrewMembers = useCallback(() => {
    const crewMemberList = [...crewMembers];
    setCrewMembers(
      crewMemberList.map((crew: ICrewMember) => {
        crew.isSelected = false;
        return crew;
      })
    );
  }, [crewMembers]);

  const handleBulkInvite = useCallback(() => {
    const crewToInvite = crewMembers.filter((crew: ICrewMember) => {
      return crew.isSelected === true;
    });
    crewToInvite.forEach((crew: ICrewMember): void => {
      emailInvitation(crew._id);
    });
    resetCrewMembers();
    setModalOpen(false);
  }, [crewMembers, emailInvitation, resetCrewMembers]);

  const getCrewAction = useCallback(async () => {
    const fetchedCrew: ICrewMember[] = crewMembers.length > 0 ? crewMembers : await getCrew();
    setCrewMembers(fetchedCrew);
    setLoadingCrew(false);
  }, [crewMembers, setCrewMembers, setLoadingCrew]);

  useEffect(() => {
    if (!crewMembers.length && !!getCrewAction) {
      getCrewAction();
    }
  }, [getCrewAction, crewMembers]);

  const updateInviteList = useCallback(
    (_id: string) => {
      const crewToUpdate = crewMembers.findIndex((crew: ICrewMember) => {
        return crew._id === _id;
      });
      const newCrewList = [...crewMembers];
      const selectedCrew = newCrewList.filter((crew: ICrewMember) => {
        return crew.isSelected === true;
      }).length;

      if (selectedCrew < inviteLimit || newCrewList[crewToUpdate].isSelected === true) {
        newCrewList[crewToUpdate].isSelected = !newCrewList[crewToUpdate].isSelected;
        setCrewMembers(newCrewList);
      }
    },
    [crewMembers]
  );

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setModalOpen(!modalOpen);
        }}
      >
        <AddIcon />
        Add New Devices To Crew Members
      </Button>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-crewmember-list"
        aria-describedby="modal-crewmember-list-all"
      >
        <Paper sx={{ width: 800, margin: "auto", p: 2 }}>
          <Grid container>
            <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
              <Alert severity="info" variant="filled">
                Please note that you can invite up to {inviteLimit} people at a time.
              </Alert>
            </Grid>
            <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
              {loadingCrew ? (
                <LoadingWrapper message="Loading Crew list" />
              ) : (
                <>
                  <Typography component="h2">Crew Members to invite ({crewMembers.length} total)</Typography>
                  <TextField
                    size="small"
                    sx={{
                      width: "100%",
                      border: `1px solid ${theme.custom.forms.inputBorder}`,
                      background: theme.custom.forms.inputBack,
                    }}
                    placeholder="Filter Crew Members"
                    variant="outlined"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  />
                  <Box sx={{ height: "60vh", overflow: "scroll" }}>
                    {crewMembers
                      .filter((crew: ICrewMember) => {
                        const filter = filterValue.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                        return (
                          crew.firstName.toLowerCase().includes(filter) ||
                          crew.lastName.toLowerCase().includes(filter) ||
                          (crew?.employeeId && crew.employeeId.includes(filter))
                        );
                      })
                      .sort((a, b) => {
                        return a.lastName.localeCompare(b.lastName);
                      })
                      .map((crew, key) => {
                        return (
                          <DraggableListItem
                            active={crew.isSelected}
                            key={key}
                            onClick={() => updateInviteList(crew._id)}
                            sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
                          >
                            {crew.lastName}, {crew.firstName} ({crew.employeeId})
                            {crew.isSelected && (
                              <Box sx={{ display: "flex", justifySelf: "end" }}>
                                <CheckIcon />
                              </Box>
                            )}
                          </DraggableListItem>
                        );
                      })}
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
          <Grid container>
            <Grid item sx={{ maxHeight: "70vh", overflowY: "scroll", width: "100%", mb: 2 }}></Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "end" }}>
              <ButtonGroup variant="contained">
                <Button color="success" sx={{ color: "white", alignSelf: "end" }} onClick={() => handleBulkInvite()}>
                  Send Invitation to{" "}
                  {
                    crewMembers.filter((crew: ICrewMember) => {
                      return crew.isSelected === true;
                    }).length
                  }{" "}
                  Crew Member(s)
                </Button>
                <Button
                  color="error"
                  sx={{ alignSelf: "end" }}
                  onClick={() => {
                    resetCrewMembers();
                    setModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
    </>
  );
};
